import { PenPathInstance } from '../../services/api';
import {
  AnchorPoint,
  LineCap,
  LineJoin,
  ArrowType,
  DashPreset,
  DASH_PATTERNS,
  getStartTangentAngle,
  getEndTangentAngle
} from '../../types/penPath';

/**
 * Converts hex color to rgba
 */
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return hex;
}

/**
 * Parses hex color to RGB components
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    };
  }
  return null;
}

/**
 * Draws a feathered (soft) edge around the current path using multiple strokes.
 * Each stroke's opacity is calculated so it doesn't exceed fillOpacity.
 * @param ctx Canvas context (must have a path already defined)
 * @param fillColor Fill color in hex format
 * @param fillOpacity Base fill opacity (0-1)
 * @param featherRadius Radius of the feather effect in pixels
 */
function drawFeatheredEdgeForCurrentPath(
  ctx: CanvasRenderingContext2D,
  fillColor: string,
  fillOpacity: number,
  featherRadius: number
): void {
  const rgb = hexToRgb(fillColor);
  if (!rgb) return;

  // Number of steps for the gradient effect - more steps for larger radius
  const steps = Math.min(Math.ceil(featherRadius / 2), 48);
  if (steps <= 0) return;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Draw strokes from outside to inside
  // Each stroke is drawn with opacity that creates a smooth gradient
  // The innermost strokes will be covered by the fill, so we only care about outer edge
  for (let i = steps; i >= 1; i--) {
    const t = i / steps; // 1 = outermost, 0 = innermost
    // Linear falloff scaled by fillOpacity - outermost is most transparent
    const alpha = fillOpacity * (1 - t) * 0.7;
    const strokeWidth = (featherRadius * 2) * t;

    ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
}

/**
 * Draws a feathered edge for inverted fill (soft edge going inward into the cutout)
 * @param ctx Canvas context (must have a path already defined)
 * @param fillColor Fill color in hex format
 * @param fillOpacity Base fill opacity (0-1)
 * @param featherRadius Radius of the feather effect in pixels
 */
function drawInvertedFeatheredEdgeForCurrentPath(
  ctx: CanvasRenderingContext2D,
  fillColor: string,
  fillOpacity: number,
  featherRadius: number
): void {
  const rgb = hexToRgb(fillColor);
  if (!rgb) return;

  // Number of steps for the gradient effect - more steps for larger radius
  const steps = Math.min(Math.ceil(featherRadius / 2), 48);
  if (steps <= 0) return;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Draw strokes from inside to outside (into the cutout area)
  // The strokes fade from solid to transparent as they go inward
  for (let i = 1; i <= steps; i++) {
    const t = i / steps; // 0 = innermost (edge), 1 = outermost (center of cutout)
    // Fade from fillOpacity at edge to 0 at center
    const alpha = fillOpacity * (1 - t);
    const strokeWidth = (featherRadius * 2) * t;

    ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
}

/**
 * Gets the length by which to shorten the path for an arrow type
 * @param type Arrow type
 * @param size Arrow size (based on stroke width)
 * @returns Distance to shorten the path
 */
function getArrowShortenDistance(type: ArrowType, size: number): number {
  if (type === 'none' || type === 'arrow') return 0; // Open arrows don't need shortening
  const arrowSize = size * 3;
  const halfSize = arrowSize / 2;
  switch (type) {
    case 'triangle': return arrowSize * 0.8; // Shorten by most of arrow length
    case 'circle': return halfSize; // Center of circle
    case 'square': return arrowSize * 0.5;
    case 'diamond': return arrowSize * 0.5;
    default: return 0;
  }
}

/**
 * Draws an arrow head at a given position
 * @param ctx Canvas context
 * @param x Position X
 * @param y Position Y
 * @param angle Arrow angle in radians
 * @param type Arrow type
 * @param size Arrow size (based on stroke width)
 * @param color Arrow color
 */
export function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  type: ArrowType,
  size: number,
  color: string
): void {
  if (type === 'none') return;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  const arrowSize = size * 3;
  const halfSize = arrowSize / 2;

  switch (type) {
    case 'arrow':
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-arrowSize, -halfSize);
      ctx.moveTo(0, 0);
      ctx.lineTo(-arrowSize, halfSize);
      ctx.stroke();
      break;

    case 'triangle':
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-arrowSize, -halfSize);
      ctx.lineTo(-arrowSize, halfSize);
      ctx.closePath();
      ctx.fill();
      break;

    case 'circle':
      ctx.beginPath();
      ctx.arc(-halfSize, 0, halfSize, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'square':
      ctx.fillRect(-arrowSize, -halfSize, arrowSize, arrowSize);
      break;

    case 'diamond':
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-halfSize, -halfSize);
      ctx.lineTo(-arrowSize, 0);
      ctx.lineTo(-halfSize, halfSize);
      ctx.closePath();
      ctx.fill();
      break;
  }

  ctx.restore();
}

/**
 * Draws a complete pen path
 * @param ctx Canvas context
 * @param path The pen path instance
 * @param zoom Current viewport zoom
 * @param offsetX Viewport offset X
 * @param offsetY Viewport offset Y
 * @param centerX Canvas center X
 * @param centerY Canvas center Y
 * @param isSelected Whether the path is selected
 * @param isEditing Whether the path is being edited (show anchor points)
 * @param selectedAnchorIndices Set of selected anchor point indices (for highlighting)
 */
export function drawPenPath(
  ctx: CanvasRenderingContext2D,
  path: PenPathInstance,
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number,
  isSelected: boolean = false,
  isEditing: boolean = false,
  selectedAnchorIndices: Set<number> = new Set()
): void {
  const points = path.points;
  if (points.length === 0) return;

  // Transform points to screen coordinates
  const screenPoints = points.map(p => ({
    ...p,
    screenX: (p.x - offsetX) * zoom + centerX,
    screenY: (p.y - offsetY) * zoom + centerY,
    handleInScreen: p.handleIn ? {
      x: ((p.x + p.handleIn.x) - offsetX) * zoom + centerX,
      y: ((p.y + p.handleIn.y) - offsetY) * zoom + centerY
    } : null,
    handleOutScreen: p.handleOut ? {
      x: ((p.x + p.handleOut.x) - offsetX) * zoom + centerX,
      y: ((p.y + p.handleOut.y) - offsetY) * zoom + centerY
    } : null
  }));

  // Build the path
  ctx.beginPath();
  ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

  for (let i = 1; i < screenPoints.length; i++) {
    const prev = screenPoints[i - 1];
    const curr = screenPoints[i];

    // Control points
    const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
    const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
    const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
    const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
  }

  // Close path if needed
  if (path.closed && screenPoints.length > 1) {
    const last = screenPoints[screenPoints.length - 1];
    const first = screenPoints[0];

    const cp1x = last.handleOutScreen ? last.handleOutScreen.x : last.screenX;
    const cp1y = last.handleOutScreen ? last.handleOutScreen.y : last.screenY;
    const cp2x = first.handleInScreen ? first.handleInScreen.x : first.screenX;
    const cp2y = first.handleInScreen ? first.handleInScreen.y : first.screenY;

    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, first.screenX, first.screenY);
    ctx.closePath();
  }

  const featherRadius = (path.featherRadius ?? 0) * zoom;
  const invertFill = path.invertFill ?? false;

  // Fill if color is set
  if (path.fillColor) {
    if (invertFill) {
      // Inverted fill: fill everything except the path (cutout effect)
      ctx.save();

      // Draw the full canvas rectangle first
      ctx.beginPath();
      ctx.rect(0, 0, ctx.canvas.width, ctx.canvas.height);

      // Then draw the path on top (will be cut out with evenodd)
      ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

      for (let i = 1; i < screenPoints.length; i++) {
        const prev = screenPoints[i - 1];
        const curr = screenPoints[i];

        const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
        const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
        const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
        const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
      }

      if (path.closed && screenPoints.length > 1) {
        const last = screenPoints[screenPoints.length - 1];
        const first = screenPoints[0];

        const cp1x = last.handleOutScreen ? last.handleOutScreen.x : last.screenX;
        const cp1y = last.handleOutScreen ? last.handleOutScreen.y : last.screenY;
        const cp2x = first.handleInScreen ? first.handleInScreen.x : first.screenX;
        const cp2y = first.handleInScreen ? first.handleInScreen.y : first.screenY;

        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, first.screenX, first.screenY);
        ctx.closePath();
      }

      // Use evenodd fill rule to create the cutout effect
      ctx.fillStyle = hexToRgba(path.fillColor, path.fillOpacity);
      ctx.fill('evenodd');

      ctx.restore();

      // Draw feathered edge going inward (into the transparent cutout area)
      if (featherRadius > 0) {
        // Rebuild path for feather stroke
        ctx.beginPath();
        ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

        for (let i = 1; i < screenPoints.length; i++) {
          const prev = screenPoints[i - 1];
          const curr = screenPoints[i];

          const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
          const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
          const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
          const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
        }

        if (path.closed && screenPoints.length > 1) {
          const last = screenPoints[screenPoints.length - 1];
          const first = screenPoints[0];

          const cp1x = last.handleOutScreen ? last.handleOutScreen.x : last.screenX;
          const cp1y = last.handleOutScreen ? last.handleOutScreen.y : last.screenY;
          const cp2x = first.handleInScreen ? first.handleInScreen.x : first.screenX;
          const cp2y = first.handleInScreen ? first.handleInScreen.y : first.screenY;

          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, first.screenX, first.screenY);
          ctx.closePath();
        }

        drawInvertedFeatheredEdgeForCurrentPath(ctx, path.fillColor, path.fillOpacity, featherRadius);
      }
    } else {
      // Normal fill
      // Draw feathered edge FIRST (underneath the fill) so it extends outward
      // without appearing more opaque than the fill when fillOpacity < 1
      if (featherRadius > 0) {
        drawFeatheredEdgeForCurrentPath(ctx, path.fillColor, path.fillOpacity, featherRadius);
      }

      // Then draw the solid fill on top
      // Need to rebuild the path after feather strokes modified it
      ctx.beginPath();
      ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

      for (let i = 1; i < screenPoints.length; i++) {
        const prev = screenPoints[i - 1];
        const curr = screenPoints[i];

        const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
        const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
        const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
        const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
      }

      if (path.closed && screenPoints.length > 1) {
        const last = screenPoints[screenPoints.length - 1];
        const first = screenPoints[0];

        const cp1x = last.handleOutScreen ? last.handleOutScreen.x : last.screenX;
        const cp1y = last.handleOutScreen ? last.handleOutScreen.y : last.screenY;
        const cp2x = first.handleInScreen ? first.handleInScreen.x : first.screenX;
        const cp2y = first.handleInScreen ? first.handleInScreen.y : first.screenY;

        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, first.screenX, first.screenY);
        ctx.closePath();
      }

      ctx.fillStyle = hexToRgba(path.fillColor, path.fillOpacity);
      ctx.fill();
    }
  }

  // Stroke if color is set (always draw stroke without blur for crisp edges)
  if (path.strokeColor) {
    // Need to rebuild path after fill with blur (filter affects path)
    ctx.beginPath();
    ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

    for (let i = 1; i < screenPoints.length; i++) {
      const prev = screenPoints[i - 1];
      const curr = screenPoints[i];

      const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
      const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
      const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
      const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
    }

    if (path.closed && screenPoints.length > 1) {
      const last = screenPoints[screenPoints.length - 1];
      const first = screenPoints[0];

      const cp1x = last.handleOutScreen ? last.handleOutScreen.x : last.screenX;
      const cp1y = last.handleOutScreen ? last.handleOutScreen.y : last.screenY;
      const cp2x = first.handleInScreen ? first.handleInScreen.x : first.screenX;
      const cp2y = first.handleInScreen ? first.handleInScreen.y : first.screenY;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, first.screenX, first.screenY);
      ctx.closePath();
    }

    ctx.strokeStyle = hexToRgba(path.strokeColor, path.strokeOpacity);
    ctx.lineWidth = path.strokeWidth * zoom;
    ctx.lineCap = path.lineCap as CanvasLineCap;
    ctx.lineJoin = path.lineJoin as CanvasLineJoin;

    // Apply dash pattern
    const dashPattern = DASH_PATTERNS[path.dashPreset as DashPreset];
    if (dashPattern) {
      ctx.setLineDash(dashPattern.map(d => d * path.strokeWidth * zoom));
    } else {
      ctx.setLineDash([]);
    }

    ctx.stroke();
    ctx.setLineDash([]); // Reset
  }

  // Draw arrows for open paths
  if (!path.closed && screenPoints.length >= 2 && path.strokeColor) {
    const strokeColor = hexToRgba(path.strokeColor, path.strokeOpacity);
    const arrowSize = path.strokeWidth * zoom;

    // Start arrow
    if (path.arrowStart !== 'none') {
      const p0 = points[0];
      const p1 = points[1];
      const angle = getStartTangentAngle(p0 as AnchorPoint, p1 as AnchorPoint);

      // Offset arrow along tangent so it aligns with stroke end
      const shortenDist = getArrowShortenDistance(path.arrowStart as ArrowType, arrowSize);
      const offsetX = Math.cos(angle + Math.PI) * shortenDist;
      const offsetY = Math.sin(angle + Math.PI) * shortenDist;

      drawArrowHead(
        ctx,
        screenPoints[0].screenX + offsetX,
        screenPoints[0].screenY + offsetY,
        angle + Math.PI, // Reverse direction for start
        path.arrowStart as ArrowType,
        arrowSize,
        strokeColor
      );
    }

    // End arrow
    if (path.arrowEnd !== 'none') {
      const pLast = points[points.length - 1];
      const pPrev = points[points.length - 2];
      const angle = getEndTangentAngle(pPrev as AnchorPoint, pLast as AnchorPoint);

      // Offset arrow along tangent so it aligns with stroke end
      const shortenDist = getArrowShortenDistance(path.arrowEnd as ArrowType, arrowSize);
      const offsetX = Math.cos(angle) * shortenDist;
      const offsetY = Math.sin(angle) * shortenDist;

      drawArrowHead(
        ctx,
        screenPoints[screenPoints.length - 1].screenX + offsetX,
        screenPoints[screenPoints.length - 1].screenY + offsetY,
        angle,
        path.arrowEnd as ArrowType,
        arrowSize,
        strokeColor
      );
    }
  }

  // Draw selection outline
  if (isSelected) {
    ctx.strokeStyle = '#00aaff';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);

    ctx.beginPath();
    ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

    for (let i = 1; i < screenPoints.length; i++) {
      const prev = screenPoints[i - 1];
      const curr = screenPoints[i];

      const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
      const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
      const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
      const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
    }

    if (path.closed && screenPoints.length > 1) {
      const last = screenPoints[screenPoints.length - 1];
      const first = screenPoints[0];

      const cp1x = last.handleOutScreen ? last.handleOutScreen.x : last.screenX;
      const cp1y = last.handleOutScreen ? last.handleOutScreen.y : last.screenY;
      const cp2x = first.handleInScreen ? first.handleInScreen.x : first.screenX;
      const cp2y = first.handleInScreen ? first.handleInScreen.y : first.screenY;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, first.screenX, first.screenY);
    }

    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Draw anchor points and handles when editing or selected
  if (isEditing || isSelected) {
    drawAnchorPointsAndHandles(ctx, screenPoints, isEditing, selectedAnchorIndices);
  }

  // Draw rotation handle when selected (but not editing individual points)
  if (isSelected && !isEditing && screenPoints.length > 0) {
    drawRotationHandle(ctx, screenPoints);
  }
}

/**
 * Draws a rotation handle above the path's bounding box
 */
function drawRotationHandle(
  ctx: CanvasRenderingContext2D,
  screenPoints: Array<{
    screenX: number;
    screenY: number;
    handleInScreen: { x: number; y: number } | null;
    handleOutScreen: { x: number; y: number } | null;
  }>
): void {
  if (screenPoints.length === 0) return;

  // Calculate bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const point of screenPoints) {
    minX = Math.min(minX, point.screenX);
    minY = Math.min(minY, point.screenY);
    maxX = Math.max(maxX, point.screenX);
    maxY = Math.max(maxY, point.screenY);
    // Include handles in bounds
    if (point.handleInScreen) {
      minX = Math.min(minX, point.handleInScreen.x);
      minY = Math.min(minY, point.handleInScreen.y);
      maxX = Math.max(maxX, point.handleInScreen.x);
      maxY = Math.max(maxY, point.handleInScreen.y);
    }
    if (point.handleOutScreen) {
      minX = Math.min(minX, point.handleOutScreen.x);
      minY = Math.min(minY, point.handleOutScreen.y);
      maxX = Math.max(maxX, point.handleOutScreen.x);
      maxY = Math.max(maxY, point.handleOutScreen.y);
    }
  }

  // Calculate center and rotation handle position
  const centerX = (minX + maxX) / 2;
  const topCenterY = minY;
  const handleDistance = 25; // Distance above bounding box
  const rotateHandleY = topCenterY - handleDistance;
  const handleSize = 8;

  // Draw line from top center to rotation handle
  ctx.strokeStyle = '#00aaff';
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(centerX, topCenterY);
  ctx.lineTo(centerX, rotateHandleY);
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw rotation handle circle
  ctx.fillStyle = '#00aaff';
  ctx.beginPath();
  ctx.arc(centerX, rotateHandleY, handleSize / 2 + 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(centerX, rotateHandleY, handleSize / 2 - 1, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Draws anchor points and their control handles
 * @param ctx Canvas context
 * @param screenPoints Array of screen-space points with handles
 * @param showHandles Whether to show bezier handles
 * @param selectedIndices Set of selected anchor point indices (for highlighting)
 */
function drawAnchorPointsAndHandles(
  ctx: CanvasRenderingContext2D,
  screenPoints: Array<{
    screenX: number;
    screenY: number;
    handleInScreen: { x: number; y: number } | null;
    handleOutScreen: { x: number; y: number } | null;
    handleMode: string;
  }>,
  showHandles: boolean,
  selectedIndices: Set<number> = new Set()
): void {
  const anchorSize = 6;
  const selectedAnchorSize = 8;
  const handleSize = 4;

  for (let i = 0; i < screenPoints.length; i++) {
    const point = screenPoints[i];
    const isAnchorSelected = selectedIndices.has(i);
    const size = isAnchorSelected ? selectedAnchorSize : anchorSize;

    // Draw handles if editing
    if (showHandles) {
      ctx.strokeStyle = '#888888';
      ctx.lineWidth = 1;

      // Handle in
      if (point.handleInScreen) {
        ctx.beginPath();
        ctx.moveTo(point.screenX, point.screenY);
        ctx.lineTo(point.handleInScreen.x, point.handleInScreen.y);
        ctx.stroke();

        // Handle point
        ctx.fillStyle = point.handleMode === 'smooth' ? '#00aaff' : '#ff6600';
        ctx.beginPath();
        ctx.arc(point.handleInScreen.x, point.handleInScreen.y, handleSize, 0, Math.PI * 2);
        ctx.fill();
      }

      // Handle out
      if (point.handleOutScreen) {
        ctx.beginPath();
        ctx.moveTo(point.screenX, point.screenY);
        ctx.lineTo(point.handleOutScreen.x, point.handleOutScreen.y);
        ctx.stroke();

        // Handle point
        ctx.fillStyle = point.handleMode === 'smooth' ? '#00aaff' : '#ff6600';
        ctx.beginPath();
        ctx.arc(point.handleOutScreen.x, point.handleOutScreen.y, handleSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw anchor point (selected anchors are larger and have different color)
    ctx.fillStyle = isAnchorSelected ? '#00aaff' : '#ffffff';
    ctx.strokeStyle = isAnchorSelected ? '#ffffff' : '#00aaff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.rect(
      point.screenX - size / 2,
      point.screenY - size / 2,
      size,
      size
    );
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * Draws a preview of the path being created
 * @param ctx Canvas context
 * @param points Current anchor points
 * @param mousePos Current mouse position in screen coordinates
 * @param tempHandle Temporary handle position during drag
 * @param settings Current pen tool settings
 * @param zoom Current viewport zoom
 * @param offsetX Viewport offset X
 * @param offsetY Viewport offset Y
 * @param centerX Canvas center X
 * @param centerY Canvas center Y
 */
export function drawPathPreview(
  ctx: CanvasRenderingContext2D,
  points: AnchorPoint[],
  mousePos: { x: number; y: number } | null,
  tempHandle: { x: number; y: number } | null,
  settings: {
    strokeColor: string | null;
    strokeWidth: number;
    strokeOpacity: number;
    lineCap: LineCap;
    lineJoin: LineJoin;
    dashPreset: DashPreset;
    fillColor: string | null;
    fillOpacity: number;
  },
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number
): void {
  if (points.length === 0) return;

  // Transform points to screen coordinates
  const screenPoints = points.map(p => ({
    screenX: (p.x - offsetX) * zoom + centerX,
    screenY: (p.y - offsetY) * zoom + centerY,
    handleInScreen: p.handleIn ? {
      x: ((p.x + p.handleIn.x) - offsetX) * zoom + centerX,
      y: ((p.y + p.handleIn.y) - offsetY) * zoom + centerY
    } : null,
    handleOutScreen: p.handleOut ? {
      x: ((p.x + p.handleOut.x) - offsetX) * zoom + centerX,
      y: ((p.y + p.handleOut.y) - offsetY) * zoom + centerY
    } : null,
    handleMode: p.handleMode
  }));

  // Get stroke style
  const strokeColor = settings.strokeColor ? hexToRgba(settings.strokeColor, settings.strokeOpacity) : 'transparent';
  const strokeWidth = settings.strokeWidth;

  // Draw the existing path
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = strokeWidth * zoom;
  ctx.lineCap = settings.lineCap;
  ctx.lineJoin = settings.lineJoin;

  // Apply dash pattern
  const dashPattern = DASH_PATTERNS[settings.dashPreset];
  if (dashPattern) {
    ctx.setLineDash(dashPattern.map(d => d * zoom));
  } else {
    ctx.setLineDash([]);
  }

  if (screenPoints.length >= 2) {
    ctx.beginPath();
    ctx.moveTo(screenPoints[0].screenX, screenPoints[0].screenY);

    for (let i = 1; i < screenPoints.length; i++) {
      const prev = screenPoints[i - 1];
      const curr = screenPoints[i];

      const cp1x = prev.handleOutScreen ? prev.handleOutScreen.x : prev.screenX;
      const cp1y = prev.handleOutScreen ? prev.handleOutScreen.y : prev.screenY;
      const cp2x = curr.handleInScreen ? curr.handleInScreen.x : curr.screenX;
      const cp2y = curr.handleInScreen ? curr.handleInScreen.y : curr.screenY;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, curr.screenX, curr.screenY);
    }

    ctx.stroke();
  }

  // Draw preview line to mouse position
  if (mousePos && screenPoints.length >= 1) {
    const lastPoint = screenPoints[screenPoints.length - 1];
    const mouseScreenX = (mousePos.x - offsetX) * zoom + centerX;
    const mouseScreenY = (mousePos.y - offsetY) * zoom + centerY;

    ctx.strokeStyle = 'rgba(0, 170, 255, 0.5)';
    ctx.lineWidth = strokeWidth * zoom;
    ctx.setLineDash([6, 4]);

    ctx.beginPath();

    const cp1x = lastPoint.handleOutScreen ? lastPoint.handleOutScreen.x : lastPoint.screenX;
    const cp1y = lastPoint.handleOutScreen ? lastPoint.handleOutScreen.y : lastPoint.screenY;

    if (tempHandle) {
      // Use temp handle as the incoming handle for the preview
      const tempHandleScreenX = (mousePos.x + tempHandle.x - offsetX) * zoom + centerX;
      const tempHandleScreenY = (mousePos.y + tempHandle.y - offsetY) * zoom + centerY;

      ctx.moveTo(lastPoint.screenX, lastPoint.screenY);
      ctx.bezierCurveTo(
        cp1x, cp1y,
        tempHandleScreenX, tempHandleScreenY,
        mouseScreenX, mouseScreenY
      );
    } else {
      ctx.moveTo(lastPoint.screenX, lastPoint.screenY);
      ctx.bezierCurveTo(
        cp1x, cp1y,
        mouseScreenX, mouseScreenY,
        mouseScreenX, mouseScreenY
      );
    }

    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Draw anchor points and handles
  drawAnchorPointsAndHandles(ctx, screenPoints, true);

  // Draw temp handle during drag
  if (tempHandle && mousePos && points.length > 0) {
    const lastPoint = points[points.length - 1];
    const lastScreenX = (lastPoint.x - offsetX) * zoom + centerX;
    const lastScreenY = (lastPoint.y - offsetY) * zoom + centerY;
    const handleScreenX = (lastPoint.x + tempHandle.x - offsetX) * zoom + centerX;
    const handleScreenY = (lastPoint.y + tempHandle.y - offsetY) * zoom + centerY;
    const oppositeHandleScreenX = (lastPoint.x - tempHandle.x - offsetX) * zoom + centerX;
    const oppositeHandleScreenY = (lastPoint.y - tempHandle.y - offsetY) * zoom + centerY;

    // Draw handle lines
    ctx.strokeStyle = '#888888';
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(oppositeHandleScreenX, oppositeHandleScreenY);
    ctx.lineTo(lastScreenX, lastScreenY);
    ctx.lineTo(handleScreenX, handleScreenY);
    ctx.stroke();

    // Draw handle points
    ctx.fillStyle = '#00aaff';
    ctx.beginPath();
    ctx.arc(handleScreenX, handleScreenY, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(oppositeHandleScreenX, oppositeHandleScreenY, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  // Draw close indicator when near first point
  if (mousePos && screenPoints.length >= 2) {
    const firstPoint = screenPoints[0];
    const mouseScreenX = (mousePos.x - offsetX) * zoom + centerX;
    const mouseScreenY = (mousePos.y - offsetY) * zoom + centerY;

    const dist = Math.sqrt(
      Math.pow(mouseScreenX - firstPoint.screenX, 2) +
      Math.pow(mouseScreenY - firstPoint.screenY, 2)
    );

    if (dist < 15) {
      // Draw close indicator
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(firstPoint.screenX, firstPoint.screenY, 10, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
}
